import { useCallback } from 'react';
import { useAuthStore } from '../store/authStore';
import { useWorkspaceStore, setWorkspaces, setCurrentWorkspace } from '../store/workspaceStore';
import { useCollectionsStore, setCollections } from '../store/collectionsStore';
import {
  useVariablesStore,
  setEnvironments,
  setSelectedEnvironment,
  setWorkspaceVariables,
  setGlobalVariables,
  setCollectionVariablesById,
} from '../store/variablesStore';
import { setCollectionTabs } from '../store/tabsStore';
import { isTabFor } from '../utils/collectionTabState';
import { workspaceService } from '../services/workspaceService';
import { collectionService } from '../services/collectionService';
import { loadWorkspaceSnapshot, saveWorkspaceSnapshot } from '../services/workspaceStoreRouter';
import { usesLocalDataPath, isCloudRestWorkspace } from '../utils/workspaceStorage';
import { restoreVarKeyCase } from '../utils/variableScopes';
import { requireWritableWorkspace } from '../plans/localGate';
import { requireApiCapacity } from '../plans/localApiUsage';
import { clearScriptEnvOverlayKeys } from '../utils/scriptEnvOverlay';

/** Keys whose value the user changed (or added) between two variable lists. */
function editedVariableKeys(before, after) {
  const prev = new Map((before || []).map((v) => [v?.key, v?.value]));
  return (after || [])
    .filter((v) => v?.key && (!prev.has(v.key) || prev.get(v.key) !== v.value))
    .map((v) => v.key);
}

/**
 * Variable-scope persistence: save from the Environments modal (saveVariableScope),
 * inline variable upserts from editors (handleVariableUpdate), and the local-
 * workspace snapshot writer (persistLocalVariableScopes). All cross-domain reads
 * go through store getState() so the callbacks stay stable.
 *
 * @param {{ toastT: Function, snapshotHydratedRef: { current: any } }} deps
 */
export function useVariablesScopes({ toastT, snapshotHydratedRef }) {
  const persistLocalVariableScopes = useCallback(async (overrides) => {
    const currentWorkspace = useWorkspaceStore.getState().currentWorkspace;
    const isAuthenticated = useAuthStore.getState().isAuthenticated;
    if (!currentWorkspace?.id || !usesLocalDataPath(currentWorkspace, isAuthenticated)) {
      return;
    }
    const vars = useVariablesStore.getState();
    const collections = useCollectionsStore.getState().collections;
    const snap = await loadWorkspaceSnapshot(currentWorkspace);
    await saveWorkspaceSnapshot(currentWorkspace, {
      workspaceName: currentWorkspace.name || snap.workspaceName || '',
      collections: snap.collections ?? collections,
      globalVariables: overrides.globalVariables ?? snap.globalVariables ?? vars.globalVariables,
      workspaceVariables:
        overrides.workspaceVariables ?? snap.workspaceVariables ?? vars.workspaceVariables,
      environments: overrides.environments ?? snap.environments ?? vars.environments,
      selectedEnvironmentId:
        overrides.selectedEnvironmentId ?? snap.selectedEnvironmentId ?? vars.selectedEnvironment?.id ?? '',
    });
    snapshotHydratedRef.current = currentWorkspace.id;
  }, [snapshotHydratedRef]);

  const saveVariableScope = useCallback(async (environmentData) => {
    const currentWorkspace = useWorkspaceStore.getState().currentWorkspace;
    const isAuthenticated = useAuthStore.getState().isAuthenticated;
    const { globalVariables, workspaceVariables, selectedEnvironment } = useVariablesStore.getState();
    let { environments } = useVariablesStore.getState();
    // Plans v2: nothing is saved into an own cloud workspace while it is
    // read-only. false = refused (EnvironmentEditor then never says "Saved").
    if (!requireWritableWorkspace(currentWorkspace)) return false;

    // A value the user just typed wins over one a script set this session
    // (scriptEnvOverlay): otherwise editing {{base_url}} here appears to do
    // nothing while an old script's value keeps applying. Only for scopes that
    // were applying when it was typed: workspace/global always do, an
    // environment only if it is the selected one. A new environment, or an
    // edit to another one, overrides nothing a script set.
    const targetEnvId = environmentData.originalId || environmentData.id;
    const before = environmentData.isWorkspaceVariables
      ? workspaceVariables
      : environmentData.isGlobal
        ? globalVariables
        : targetEnvId && targetEnvId === selectedEnvironment?.id
          ? (environments || []).find((e) => e.id === targetEnvId)?.variables
          : null;
    if (before) clearScriptEnvOverlayKeys(editedVariableKeys(before, environmentData.variables));

    if (environmentData.isWorkspaceVariables) {
      setWorkspaceVariables(environmentData.variables);

      if (usesLocalDataPath(currentWorkspace, isAuthenticated)) {
        try {
          await persistLocalVariableScopes({ workspaceVariables: environmentData.variables });
        } catch (error) {
          toastT('toast.saveWorkspaceVarsFailed', 'error', { message: error.message });
        }
        return null;
      }

      if (isCloudRestWorkspace(currentWorkspace, isAuthenticated)) {
        try {
          const response = await workspaceService.updateVariableScopes(currentWorkspace.id, {
            globals: globalVariables,
            workspace_globals: environmentData.variables,
            environments,
          });
          const variableScopes = response.variable_scopes || {};
          const updatedSettings = response.settings || {};
          setGlobalVariables(variableScopes.globals || globalVariables);
          setWorkspaceVariables(variableScopes.workspace_globals || environmentData.variables);
          setEnvironments(variableScopes.environments || environments);
          setWorkspaces((prev) => prev.map((ws) =>
            ws.id === currentWorkspace.id
              ? { ...ws, settings: updatedSettings, variable_scopes: variableScopes }
              : ws
          ));
          setCurrentWorkspace((prev) => prev && prev.id === currentWorkspace.id
            ? { ...prev, settings: updatedSettings, variable_scopes: variableScopes }
            : prev
          );
        } catch (error) {
          toastT('toast.persistWorkspaceVarsFailed', 'error', { message: error.message });
        }
      }
      return null;
    }

    if (environmentData.isGlobal) {
      setGlobalVariables(environmentData.variables);

      if (usesLocalDataPath(currentWorkspace, isAuthenticated)) {
        try {
          await persistLocalVariableScopes({ globalVariables: environmentData.variables });
        } catch (error) {
          toastT('toast.saveGlobalVarsFailed', 'error', { message: error.message });
        }
        return null;
      }

      if (isCloudRestWorkspace(currentWorkspace, isAuthenticated)) {
        try {
          await workspaceService.updateVariableScopes(currentWorkspace.id, {
            globals: environmentData.variables,
            workspace_globals: workspaceVariables,
            environments,
          });
        } catch (error) {
          toastT('toast.persistGlobalVarsFailed', 'error', { message: error.message });
        }
      }
      return null;
    }

    // Plans v2: a NEW environment counts (Free has 1); edits never do.
    const isNewEnvironment = !environmentData.originalId &&
      !environments.some((env) => env.id === environmentData.id);
    if (isNewEnvironment && !(await requireApiCapacity('environments'))) return false;
    // Re-read: the count above awaited the other workspaces' snapshots.
    ({ environments } = useVariablesStore.getState());

    const normalizedEnv = {
      id: environmentData.originalId || environmentData.id || Date.now().toString(),
      name: environmentData.name,
      variables: environmentData.variables || [],
    };

    const nextEnvironments = environmentData.originalId
      ? environments.map((env) => (env.id === environmentData.originalId ? normalizedEnv : env))
      : [...environments, normalizedEnv];

    setEnvironments(nextEnvironments);
    setSelectedEnvironment(normalizedEnv);

    if (usesLocalDataPath(currentWorkspace, isAuthenticated)) {
      try {
        await persistLocalVariableScopes({
          environments: nextEnvironments,
          selectedEnvironmentId: normalizedEnv.id,
        });
      } catch (error) {
        toastT('toast.saveEnvironmentFailed', 'error', { message: error.message });
      }
      return normalizedEnv;
    }

    if (!isCloudRestWorkspace(currentWorkspace, isAuthenticated)) {
      return normalizedEnv;
    }

    try {
      const response = await workspaceService.updateVariableScopes(currentWorkspace.id, {
        globals: globalVariables,
        workspace_globals: workspaceVariables,
        environments: nextEnvironments,
      });

      const variableScopes = response.variable_scopes || {};
      const updatedSettings = response.settings || {};

      setGlobalVariables(restoreVarKeyCase(globalVariables, variableScopes.globals) || globalVariables);
      setWorkspaceVariables(restoreVarKeyCase(workspaceVariables, variableScopes.workspace_globals) || workspaceVariables);
      setEnvironments(variableScopes.environments || nextEnvironments);
      setWorkspaces((prev) => prev.map((ws) =>
        ws.id === currentWorkspace.id
          ? { ...ws, settings: updatedSettings, variable_scopes: variableScopes }
          : ws
      ));
      setCurrentWorkspace((prev) => prev && prev.id === currentWorkspace.id
        ? { ...prev, settings: updatedSettings, variable_scopes: variableScopes }
        : prev
      );
    } catch (error) {
      toastT('toast.persistEnvironmentFailed', 'error', { message: error.message });
    }
    return normalizedEnv;
  }, [toastT, persistLocalVariableScopes]);

  // Remove a variable from a scope it was moved out of, so a scope switch doesn't leave a stale
  // duplicate behind (getVariableValue resolves environment > collection > workspace > global, so a
  // lingering higher-priority copy would keep winning). Mirrors the persistence shape of the
  // matching upsert branches in handleVariableUpdate below — optimistic setState plus a backend
  // save for backend workspaces.
  const removeVariableFromScope = useCallback(async (scope, variableKey, collectionId) => {
    const key = (variableKey ?? '').toLowerCase();
    if (!key) return;

    const currentWorkspace = useWorkspaceStore.getState().currentWorkspace;
    const isAuthenticated = useAuthStore.getState().isAuthenticated;
    const { globalVariables, workspaceVariables, environments, selectedEnvironment, collectionVariablesById } =
      useVariablesStore.getState();
    const without = (list) => (list || []).filter((v) => (v.key ?? '').toLowerCase() !== key);

    if (scope === 'collection') {
      if (!collectionId) return;
      const nextCollectionVariables = without(collectionVariablesById[collectionId]);
      setCollectionVariablesById((prev) => ({ ...prev, [collectionId]: nextCollectionVariables }));

      const applyLocalCollectionVariables = () => {
        setCollections((prev) =>
          prev.map((c) =>
            String(c.id) === String(collectionId)
              ? {
                  ...c,
                  settings: { ...(c.settings || {}), collection_variables: nextCollectionVariables },
                  variable_scopes: { ...(c.variable_scopes || {}), collection_variables: nextCollectionVariables },
                }
              : c
          )
        );
        setCollectionTabs((prev) =>
          prev.map((tab) =>
            // The collection's own page — a folder page's `variables` are the folder's draft.
            isTabFor(tab, collectionId)
              ? { ...tab, variables: nextCollectionVariables }
              : tab
          )
        );
      };

      if (!isCloudRestWorkspace(currentWorkspace, isAuthenticated)) {
        applyLocalCollectionVariables();
        return;
      }
      try {
        await collectionService.updateVariableScopes(collectionId, {
          collection_variables: nextCollectionVariables,
        });
        applyLocalCollectionVariables();
      } catch (error) {
        toastT('toast.saveCollectionVarFailed', 'error', { message: error.message });
      }
      return;
    }

    if (scope === 'global') {
      const nextGlobals = without(globalVariables);
      setGlobalVariables(nextGlobals);
      if (isCloudRestWorkspace(currentWorkspace, isAuthenticated)) {
        try {
          await workspaceService.updateVariableScopes(currentWorkspace.id, {
            globals: nextGlobals,
            workspace_globals: workspaceVariables,
            environments,
          });
        } catch (error) {
          toastT('toast.saveGlobalVarFailed', 'error', { message: error.message });
        }
      }
      return;
    }

    if (scope === 'workspace') {
      const nextWorkspaceVariables = without(workspaceVariables);
      setWorkspaceVariables(nextWorkspaceVariables);
      if (isCloudRestWorkspace(currentWorkspace, isAuthenticated)) {
        try {
          await workspaceService.updateVariableScopes(currentWorkspace.id, {
            globals: globalVariables,
            workspace_globals: nextWorkspaceVariables,
            environments,
          });
        } catch (error) {
          toastT('toast.saveWorkspaceVarFailed', 'error', { message: error.message });
        }
      }
      return;
    }

    // environment
    if (!selectedEnvironment) return;
    const nextEnvironments = environments.map((env) =>
      env.id === selectedEnvironment.id ? { ...env, variables: without(env.variables) } : env
    );
    const nextSelectedEnvironment = nextEnvironments.find((env) => env.id === selectedEnvironment.id) || null;
    setEnvironments(nextEnvironments);
    setSelectedEnvironment(nextSelectedEnvironment);
    if (isCloudRestWorkspace(currentWorkspace, isAuthenticated)) {
      try {
        await workspaceService.updateVariableScopes(currentWorkspace.id, {
          globals: globalVariables,
          workspace_globals: workspaceVariables,
          environments: nextEnvironments,
        });
      } catch (error) {
        toastT('toast.saveEnvVarFailed', 'error', { message: error.message });
      }
    }
  }, [toastT]);

  const handleVariableUpdate = useCallback(async (variable, updateContext = {}) => {
    // An explicit edit of this key wins over a script's session value.
    // Case-insensitive, like the upsert below: editing `Token` replaces `token`.
    if (variable?.key) clearScriptEnvOverlayKeys([variable.key], { ignoreCase: true });
    const scope = typeof updateContext === 'string' ? updateContext : updateContext.scope;
    const collectionId = typeof updateContext === 'object' ? updateContext.collectionId : null;
    const previousScope = typeof updateContext === 'object' ? updateContext.previousScope : null;
    const previousCollectionId = typeof updateContext === 'object' ? updateContext.previousCollectionId : null;

    // Scope changed for an existing variable: drop the old copy first (and let its persistence
    // settle) so the upsert below reads post-removal state and the variable doesn't end up living
    // in both scopes.
    if (previousScope && previousScope !== scope) {
      await removeVariableFromScope(previousScope, variable.key, previousCollectionId);
    }

    const currentWorkspace = useWorkspaceStore.getState().currentWorkspace;
    const isAuthenticated = useAuthStore.getState().isAuthenticated;
    const { globalVariables, workspaceVariables, environments, selectedEnvironment, collectionVariablesById } =
      useVariablesStore.getState();

    // Case-insensitive lookup so API-lowercased keys don't create duplicate entries.
    const upsertVariable = (source, nextVariable) => {
      const list = source || [];
      const existingIndex = list.findIndex(
        (v) => (v.key ?? '').toLowerCase() === (nextVariable.key ?? '').toLowerCase()
      );
      if (existingIndex >= 0) {
        const nextList = [...list];
        nextList[existingIndex] = nextVariable; // preserves nextVariable.key casing
        return nextList;
      }
      return [...list, nextVariable];
    };

    // Re-apply locally-typed key casing over an API-returned variable list.
    const restoreKeyCase = (local, apiVars) => {
      if (!Array.isArray(apiVars) || !Array.isArray(local)) return apiVars ?? local;
      return apiVars.map((apiVar) => {
        const match = local.find(
          (lv) => (lv.key ?? '').toLowerCase() === (apiVar.key ?? '').toLowerCase()
        );
        return match ? { ...apiVar, key: match.key } : apiVar;
      });
    };

    const hasScopeChanged = (nextScopes, currentScopes) =>
      JSON.stringify(nextScopes) !== JSON.stringify(currentScopes);

    if (scope === 'global') {
      const nextGlobals = upsertVariable(globalVariables, variable);
      setGlobalVariables(nextGlobals);

      if (isCloudRestWorkspace(currentWorkspace, isAuthenticated)) {
        try {
          const response = await workspaceService.updateVariableScopes(currentWorkspace.id, {
            globals: nextGlobals,
            workspace_globals: workspaceVariables,
            environments,
          });
          const variableScopes = response.variable_scopes || {};
          const settings = response.settings || {};
          const restoredGlobals = restoreKeyCase(nextGlobals, variableScopes.globals);
          if (hasScopeChanged(restoredGlobals || nextGlobals, nextGlobals)) {
            setGlobalVariables(restoredGlobals || nextGlobals);
          }
          if (hasScopeChanged(variableScopes.workspace_globals || workspaceVariables, workspaceVariables)) {
            const persistedWs = restoreKeyCase(workspaceVariables, variableScopes.workspace_globals) || workspaceVariables;
            setWorkspaceVariables(persistedWs);
          }
          if (hasScopeChanged(variableScopes.environments || environments, environments)) {
            setEnvironments(variableScopes.environments || environments);
          }
          setWorkspaces((prev) => prev.map((ws) =>
            ws.id === currentWorkspace.id ? { ...ws, settings, variable_scopes: variableScopes } : ws
          ));
          setCurrentWorkspace((prev) => prev && prev.id === currentWorkspace.id
            ? { ...prev, settings, variable_scopes: variableScopes }
            : prev
          );
        } catch (error) {
          toastT('toast.saveGlobalVarFailed', 'error', { message: error.message });
        }
      }
      return;
    }

    if (scope === 'collection') {
      if (!collectionId) {
        toastT('toast.collectionScopeRequired', 'warning');
        return;
      }

      const nextCollectionVariables = upsertVariable(collectionVariablesById[collectionId], variable);
      setCollectionVariablesById((prev) => ({ ...prev, [collectionId]: nextCollectionVariables }));

      const applyLocalCollectionVariables = () => {
        setCollections((prev) =>
          prev.map((c) =>
            String(c.id) === String(collectionId)
              ? {
                  ...c,
                  settings: {
                    ...(c.settings || {}),
                    collection_variables: nextCollectionVariables,
                  },
                  variable_scopes: {
                    ...(c.variable_scopes || {}),
                    collection_variables: nextCollectionVariables,
                  },
                }
              : c
          )
        );
        setCollectionTabs((prev) =>
          prev.map((tab) =>
            // The collection's own page — a folder page's `variables` are the folder's draft.
            isTabFor(tab, collectionId)
              ? { ...tab, variables: nextCollectionVariables }
              : tab
          )
        );
      };

      if (!isCloudRestWorkspace(currentWorkspace, isAuthenticated)) {
        applyLocalCollectionVariables();
        return;
      }

      try {
        const response = await collectionService.updateVariableScopes(collectionId, {
          collection_variables: nextCollectionVariables,
        });
        const persisted = restoreKeyCase(
          nextCollectionVariables,
          response.variable_scopes?.collection_variables
        ) || nextCollectionVariables;
        setCollectionVariablesById((prev) => ({ ...prev, [collectionId]: persisted }));
        applyLocalCollectionVariables();
      } catch (error) {
        toastT('toast.saveCollectionVarFailed', 'error', { message: error.message });
      }
      return;
    }

    if (scope === 'workspace') {
      if (!currentWorkspace?.id) {
        toastT('toast.noWorkspaceForVar', 'warning');
        return;
      }

      const nextWorkspaceVariables = upsertVariable(workspaceVariables, variable);
      setWorkspaceVariables(nextWorkspaceVariables);

      try {
        if (!isCloudRestWorkspace(currentWorkspace, isAuthenticated)) {
          return;
        }
        const response = await workspaceService.updateVariableScopes(currentWorkspace.id, {
          globals: globalVariables,
          workspace_globals: nextWorkspaceVariables,
          environments,
        });
        const variableScopes = response.variable_scopes || {};
        const settings = response.settings || {};
        if (hasScopeChanged(variableScopes.globals || globalVariables, globalVariables)) {
          setGlobalVariables(restoreKeyCase(globalVariables, variableScopes.globals) || globalVariables);
        }
        const persistedWs = restoreKeyCase(nextWorkspaceVariables, variableScopes.workspace_globals) || nextWorkspaceVariables;
        if (hasScopeChanged(persistedWs, nextWorkspaceVariables)) {
          setWorkspaceVariables(persistedWs);
        }
        setWorkspaces((prev) => prev.map((ws) =>
          ws.id === currentWorkspace.id ? { ...ws, settings, variable_scopes: variableScopes } : ws
        ));
        setCurrentWorkspace((prev) => prev && prev.id === currentWorkspace.id
          ? { ...prev, settings, variable_scopes: variableScopes }
          : prev
        );
      } catch (error) {
        toastT('toast.saveWorkspaceVarFailed', 'error', { message: error.message });
      }
      return;
    }

    if (!selectedEnvironment) {
      toastT('toast.selectEnvironmentForVar', 'warning');
      return;
    }

    const nextEnvironments = environments.map((env) => {
      if (env.id !== selectedEnvironment.id) return env;
      return { ...env, variables: upsertVariable(env.variables, variable) };
    });

    const nextSelectedEnvironment = nextEnvironments.find((env) => env.id === selectedEnvironment.id) || null;
    setEnvironments(nextEnvironments);
    setSelectedEnvironment(nextSelectedEnvironment);

    if (!isCloudRestWorkspace(currentWorkspace, isAuthenticated)) {
      return;
    }

    try {
      const response = await workspaceService.updateVariableScopes(currentWorkspace.id, {
        globals: globalVariables,
        workspace_globals: workspaceVariables,
        environments: nextEnvironments,
      });
      const variableScopes = response.variable_scopes || {};
      const settings = response.settings || {};
      if (hasScopeChanged(variableScopes.globals || globalVariables, globalVariables)) {
        setGlobalVariables(variableScopes.globals || globalVariables);
      }
      if (hasScopeChanged(variableScopes.environments || nextEnvironments, nextEnvironments)) {
        setEnvironments(variableScopes.environments || nextEnvironments);
      }
      if (hasScopeChanged(variableScopes.workspace_globals || workspaceVariables, workspaceVariables)) {
        setWorkspaceVariables(variableScopes.workspace_globals || workspaceVariables);
      }
      setWorkspaces((prev) => prev.map((ws) =>
        ws.id === currentWorkspace.id ? { ...ws, settings, variable_scopes: variableScopes } : ws
      ));
      setCurrentWorkspace((prev) => prev && prev.id === currentWorkspace.id
        ? { ...prev, settings, variable_scopes: variableScopes }
        : prev
      );
    } catch (error) {
      toastT('toast.saveEnvVarFailed', 'error', { message: error.message });
    }
  }, [toastT, removeVariableFromScope]);

  return { persistLocalVariableScopes, saveVariableScope, handleVariableUpdate };
}
